/**
 * "How Commonplace works" — full-screen welcome overlay shown ONCE on first
 * app open. Ported from CommonplaceDriver.v5-production.jsx (WelcomeModal):
 * eyebrow + title + subtitle, a vertical numbered timeline of 4 steps with
 * maroon numbered badges, soft icon chips and a connector line, then a black
 * "Get started" button that dismisses + persists the seen flag.
 *
 * Styled with RD design tokens to match the warm/premium driver-app look.
 */
import React from 'react';
import {
  Modal, View, Text, StyleSheet, ScrollView, TouchableOpacity, Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { RD, fonts } from '../theme/colors';

interface Props {
  visible: boolean;
  firstName?: string;
  onClose: () => void;
}

const STEPS: { icon: keyof typeof Ionicons.glyphMap; title: string; desc: string }[] = [
  {
    icon: 'clipboard-outline',
    title: 'Find a job',
    desc: 'Browse white-glove deliveries near you. Every listing shows the item, route, pickup windows, crew needed and the exact payout, before you accept.',
  },
  {
    icon: 'school-outline',
    title: 'Get certified',
    desc: 'Specialty items like Pelotons and saunas need a short course. Pass the quiz once and that category stays unlocked for you.',
  },
  {
    icon: 'camera-outline',
    title: 'Pick up & inspect',
    desc: "At pickup, photograph the item, run the category checklist, and capture the seller's signature, all in the app.",
  },
  {
    icon: 'wallet-outline',
    title: 'Deliver & get paid',
    desc: 'Navigate to the buyer, drop off and confirm. Your set payout is released straight to your account.',
  },
];

export function WelcomeModal({ visible, firstName, onClose }: Props) {
  const name = firstName || 'Driver';
  return (
    <Modal visible={visible} animationType="fade" onRequestClose={onClose} statusBarTranslucent>
      <View style={styles.screen}>
        <ScrollView
          style={styles.scroll}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <Text style={styles.eyebrow}>WELCOME, {name.toUpperCase()}</Text>
          <Text style={styles.title}>How Commonplace works</Text>
          <Text style={styles.subtitle}>Four steps from finding a job to getting paid.</Text>

          <View style={styles.timeline}>
            {STEPS.map((step, i) => (
              <View key={step.title} style={styles.row}>
                {/* Badge + connector column */}
                <View style={styles.markerCol}>
                  <View style={styles.chip}>
                    <Ionicons name={step.icon} size={22} color={RD.maroon} />
                    <View style={styles.badge}>
                      <Text style={styles.badgeText}>{i + 1}</Text>
                    </View>
                  </View>
                  {i < STEPS.length - 1 && <View style={styles.connector} />}
                </View>
                {/* Copy */}
                <View style={styles.copyCol}>
                  <Text style={styles.stepTitle}>{step.title}</Text>
                  <Text style={styles.stepDesc}>{step.desc}</Text>
                </View>
              </View>
            ))}
          </View>
        </ScrollView>

        <View style={styles.footer}>
          <TouchableOpacity style={styles.button} onPress={onClose} activeOpacity={0.85}>
            <Text style={styles.buttonText}>Get started</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen:        { flex: 1, backgroundColor: RD.surface },
  scroll:        { flex: 1 },
  scrollContent: { paddingHorizontal: 22, paddingTop: Platform.OS === 'ios' ? 64 : 44, paddingBottom: 18 },

  eyebrow:       { fontSize: 11, fontWeight: '700', color: RD.maroon, textTransform: 'uppercase', letterSpacing: 0.8, fontFamily: fonts.semiBold },
  title:         { fontSize: 24, fontWeight: '700', color: RD.text, letterSpacing: -0.5, marginTop: 6, fontFamily: fonts.heavy },
  subtitle:      { fontSize: 13.5, color: RD.textMuted, marginTop: 5, marginBottom: 22, fontFamily: fonts.regular },

  timeline:      { paddingLeft: 8 },
  row:           { flexDirection: 'row', gap: 14 },
  markerCol:     { alignItems: 'center', flexShrink: 0 },
  chip:          { width: 44, height: 44, borderRadius: 13, backgroundColor: RD.maroonSoft, justifyContent: 'center', alignItems: 'center' },
  badge:         { position: 'absolute', top: -6, left: -6, width: 20, height: 20, borderRadius: 10, backgroundColor: RD.maroon, justifyContent: 'center', alignItems: 'center' },
  badgeText:     { color: '#fff', fontSize: 10.5, fontWeight: '700', fontFamily: fonts.semiBold },
  connector:     { width: 2, flex: 1, minHeight: 16, backgroundColor: RD.g10, marginTop: 5 },
  copyCol:       { flex: 1, paddingBottom: 16 },
  stepTitle:     { fontSize: 16, fontWeight: '700', color: RD.text, fontFamily: fonts.heavy },
  stepDesc:      { fontSize: 13, color: RD.textSec, lineHeight: 19, marginTop: 3, fontFamily: fonts.regular },

  footer:        { paddingHorizontal: 22, paddingTop: 12, paddingBottom: Platform.OS === 'ios' ? 34 : 26, borderTopWidth: 1, borderTopColor: RD.border, backgroundColor: RD.surface },
  button:        { height: 54, borderRadius: 16, backgroundColor: RD.ink, justifyContent: 'center', alignItems: 'center' },
  buttonText:    { fontSize: 16, fontWeight: '600', color: '#fff', fontFamily: fonts.semiBold },
});
